// Client for the server auth / per-user storage API. Every call sends the session cookie
// (credentials:"include") so the httpOnly cookie set at login authenticates the request.
export interface AuthUser {
  id: string; name: string; email: string;
  whiteLabel?: boolean; brandName?: string; hideWpTags?: boolean;
  // Set only on an agency team-seat account: the owning account's email (⇒ show "Team Member
  // Account"), plus which settings tabs the owner hid for this seat.
  ownerEmail?: string; hideApiKeysTab?: boolean; hideWpTab?: boolean;
}
// A team seat as shown to its owner in the seat-management UI.
export interface Seat { id: string; email: string; hideApiKeysTab: boolean; hideWpTab: boolean; }
export interface SiteSummary { id: string; label: string; siteUrl: string; username: string; addedAt: string; gscConnected: boolean; gscProperty: string; gaConnected: boolean; gaProperty: string; gbpConnected: boolean; gbpCid: string; gbpBusinessName: string; }
export interface GscProperty { siteUrl: string; permissionLevel: string; }
export interface GaProperty { property: string; displayName: string; account: string; }
export interface SiteFull extends SiteSummary { appPassword: string; }
export interface ApiKeys { anthropic: string; ahrefs: string; dfsLogin: string; dfsPassword: string; brightdataToken: string; brightdataZone: string; }

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  const data = await res.json().catch(() => ({})) as Record<string, unknown>;
  if (!res.ok) throw new Error((data.error as string) || `Request failed (${res.status})`);
  return data as T;
}

// ── Auth ──────────────────────────────────────────────────────
export async function getMe(): Promise<AuthUser | null> {
  return (await getSession()).user;
}

// Like getMe but distinguishes "not logged in" (401) from "account suspended" (403), surfacing the
// server's reason so the login screen can explain why a mid-session user was bounced.
export interface SessionResult { user: AuthUser | null; reason?: string; }
export async function getSession(): Promise<SessionResult> {
  try {
    const res = await fetch("/api/auth/me", { credentials: "include", headers: { "Content-Type": "application/json" } });
    if (res.ok) {
      const data = await res.json().catch(() => ({})) as { user?: AuthUser };
      return { user: data.user ?? null };
    }
    if (res.status === 403) {
      const data = await res.json().catch(() => ({})) as { error?: string };
      return { user: null, reason: data.error || "Your account is not active." };
    }
    return { user: null }; // 401 / other → simply not logged in
  } catch {
    return { user: null };
  }
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const { user } = await req<{ user: AuthUser }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
  return user;
}

export async function logout(): Promise<void> {
  await req("/api/auth/logout", { method: "POST" });
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await req("/api/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) });
}

// Agency (white-label) only: toggle whether the tool hides its "LSG …" marker tags on created pages.
// Persisted on the account (applies to every connected site). Returns the updated user.
export async function setHideWpTags(hideWpTags: boolean): Promise<AuthUser> {
  const { user } = await req<{ user: AuthUser }>("/api/auth/wp-tags", { method: "POST", body: JSON.stringify({ hideWpTags }) });
  return user;
}

// ── Agency team seats (owner-only) ────────────────────────────
export async function listSeats(): Promise<Seat[]> {
  const { seats } = await req<{ seats: Seat[] }>("/api/auth/seats");
  return seats;
}
// Returns the new seat plus whether an invite email was actually sent (false when SMTP isn't
// configured — the owner then shares the login manually).
export async function createSeat(payload: { email: string; password: string; hideApiKeysTab?: boolean; hideWpTab?: boolean }): Promise<{ seat: Seat; invited: boolean }> {
  return req<{ seat: Seat; invited: boolean }>("/api/auth/seats", { method: "POST", body: JSON.stringify(payload) });
}
export async function updateSeat(id: string, patch: { email?: string; password?: string; hideApiKeysTab?: boolean; hideWpTab?: boolean }): Promise<Seat> {
  const { seat } = await req<{ seat: Seat }>(`/api/auth/seats/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  return seat;
}
export async function deleteSeat(id: string): Promise<void> {
  await req(`/api/auth/seats/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// A strong, easy-to-copy password for a new seat (mirrors the server's temp-password style). Uses the
// Web Crypto RNG so it's cryptographically random.
export function generatePassword(length = 16): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) out += alphabet[bytes[i] % alphabet.length];
  return out;
}

// Request a password-reset link by email. Always resolves (the server returns a generic success
// regardless of whether the email has an account) — never reveals whether the account exists.
export async function forgotPassword(email: string): Promise<void> {
  await req("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
}

// Redeem a reset token (from the emailed link) and set a new password. Throws on invalid/expired.
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await req("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ token, newPassword }) });
}

// ── Site Storage ──────────────────────────────────────────────
export async function listSites(): Promise<SiteSummary[]> {
  const { sites } = await req<{ sites: SiteSummary[] }>("/api/sites");
  return sites;
}

export async function getSite(id: string): Promise<SiteFull> {
  const { site } = await req<{ site: SiteFull }>(`/api/sites/${encodeURIComponent(id)}`);
  return site;
}

export async function addSite(payload: { label?: string; siteUrl: string; username: string; appPassword: string }): Promise<SiteSummary> {
  const { site } = await req<{ site: SiteSummary }>("/api/sites", { method: "POST", body: JSON.stringify(payload) });
  return site;
}

export async function updateSiteCredentials(id: string, payload: { siteUrl: string; username: string; appPassword: string; label?: string }): Promise<SiteSummary> {
  const { site } = await req<{ site: SiteSummary }>(`/api/sites/${encodeURIComponent(id)}/credentials`, { method: "PUT", body: JSON.stringify(payload) });
  return site;
}

export async function removeSite(id: string): Promise<void> {
  await req(`/api/sites/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ── Per-site Google Search Console ────────────────────────────
// Exchange the OAuth code (obtained via the consent popup) for a refresh token,
// stored server-side against the site; returns the account's available properties.
export async function connectSiteGsc(id: string, code: string, redirectUri: string): Promise<GscProperty[]> {
  const { sites } = await req<{ sites: GscProperty[] }>(`/api/sites/${encodeURIComponent(id)}/gsc/connect`, {
    method: "POST", body: JSON.stringify({ code, redirectUri }),
  });
  return sites ?? [];
}

export async function listSiteGscProperties(id: string): Promise<GscProperty[]> {
  const { sites } = await req<{ sites: GscProperty[] }>(`/api/sites/${encodeURIComponent(id)}/gsc/properties`);
  return sites ?? [];
}

export async function saveSiteGscProperty(id: string, property: string): Promise<void> {
  await req(`/api/sites/${encodeURIComponent(id)}/gsc/property`, { method: "PUT", body: JSON.stringify({ property }) });
}

export async function disconnectSiteGsc(id: string): Promise<void> {
  await req(`/api/sites/${encodeURIComponent(id)}/gsc`, { method: "DELETE" });
}

// ── Per-site Google Analytics (GA4) ───────────────────────────
export async function connectSiteGa(id: string, code: string, redirectUri: string): Promise<GaProperty[]> {
  const { properties } = await req<{ properties: GaProperty[] }>(`/api/sites/${encodeURIComponent(id)}/ga/connect`, {
    method: "POST", body: JSON.stringify({ code, redirectUri }),
  });
  return properties ?? [];
}

export async function listSiteGaProperties(id: string): Promise<GaProperty[]> {
  const { properties } = await req<{ properties: GaProperty[] }>(`/api/sites/${encodeURIComponent(id)}/ga/properties`);
  return properties ?? [];
}

export async function saveSiteGaProperty(id: string, property: string): Promise<void> {
  await req(`/api/sites/${encodeURIComponent(id)}/ga/property`, { method: "PUT", body: JSON.stringify({ property }) });
}

export async function disconnectSiteGa(id: string): Promise<void> {
  await req(`/api/sites/${encodeURIComponent(id)}/ga`, { method: "DELETE" });
}

// ── LLM traffic (GA4), keyed by site host ─────────────────────
export type GaLlmPlatform = "chatgpt" | "perplexity" | "gemini" | "claude";
export interface GaTrafficRow { date: string; platform: GaLlmPlatform; sessions: number; engagedSessions: number; }
export interface GaTrafficTotal { platform: GaLlmPlatform; sessions: number; engagedSessions: number; }

export async function gaStatus(siteKey: string): Promise<{ configured: boolean; connected: boolean; property: string }> {
  const r = await req<{ configured?: boolean; connected?: boolean; property?: string }>(`/api/ga/status?siteKey=${encodeURIComponent(siteKey)}`);
  return { configured: !!r.configured, connected: !!r.connected, property: r.property ?? "" };
}

export async function gaLlmTraffic(siteKey: string, startDate: string, endDate: string): Promise<{ daily: GaTrafficRow[]; totals: GaTrafficTotal[] }> {
  const r = await req<{ daily?: GaTrafficRow[]; totals?: GaTrafficTotal[] }>("/api/ga/traffic", {
    method: "POST", body: JSON.stringify({ siteKey, startDate, endDate }),
  });
  return { daily: r.daily ?? [], totals: r.totals ?? [] };
}

// ── Low-Hanging KW Insertion ──────────────────────────────────
export interface LowHangingKw { query: string; position: number; impressions: number; }
export interface LowHangingPage { page: string; avgPosition: number; impressions: number; keywords: LowHangingKw[]; }

export async function lowHangingKeywords(id: string, brandedKeywords: string[], anthropicKey?: string): Promise<LowHangingPage[]> {
  const { pages } = await req<{ pages: LowHangingPage[] }>(`/api/sites/${encodeURIComponent(id)}/gsc/low-hanging`, {
    method: "POST", body: JSON.stringify({ brandedKeywords, anthropicKey }),
  });
  return pages ?? [];
}

// ── Cannibalization tab GSC analyses ──────────────────────────
export interface CannibalPage { page: string; clicks: number; impressions: number; position: number | null; }
export interface CannibalQuery { query: string; totalImpressions: number; totalClicks: number; pages: CannibalPage[]; }
export interface FluxPage { page: string; status: "Dropped Out" | "Newly Appeared" | "Intermittent"; daysVisible: number; impressions: number; avgPosition: number | null; }

export async function cannibalizationReport(id: string): Promise<{ cannibalization: CannibalQuery[]; flux: FluxPage[] }> {
  const r = await req<{ cannibalization?: CannibalQuery[]; flux?: FluxPage[] }>(`/api/sites/${encodeURIComponent(id)}/gsc/cannibalization`, { method: "POST", body: "{}" });
  return { cannibalization: r.cannibalization ?? [], flux: r.flux ?? [] };
}

// ── LLM Mention Tracking ──────────────────────────────────────
export type LlmPlatform = "chatgpt" | "claude" | "gemini" | "perplexity" | "google_ai_overview";
export type LlmFrequency = "daily" | "weekly" | "monthly";
export interface LlmTrackedQuery {
  id: string; siteKey: string; brand: string; query: string; platforms: LlmPlatform[];
  frequency: LlmFrequency; country: string; enabled: boolean; nextRunAt: string; lastRunAt: string | null; createdAt: string;
}
export interface LlmSnapshot { id: string; queryId: string; platform: LlmPlatform; capturedAt: string; mentions: number; citedDomains: string[]; }
export interface LlmTrackingData { queries: LlmTrackedQuery[]; snapshots: LlmSnapshot[]; }

export async function getLlmTracking(siteKey: string): Promise<LlmTrackingData> {
  const r = await req<{ queries?: LlmTrackedQuery[]; snapshots?: LlmSnapshot[] }>(`/api/llm-tracking?siteKey=${encodeURIComponent(siteKey)}`);
  return { queries: r.queries ?? [], snapshots: r.snapshots ?? [] };
}
export async function addLlmTrackedQuery(payload: { siteKey: string; brand: string; query: string; platforms: LlmPlatform[]; frequency: LlmFrequency; country?: string }): Promise<LlmTrackedQuery> {
  const r = await req<{ query: LlmTrackedQuery }>("/api/llm-tracking/queries", { method: "POST", body: JSON.stringify(payload) });
  return r.query;
}
export async function updateLlmTrackedQuery(qid: string, patch: Partial<{ query: string; brand: string; platforms: LlmPlatform[]; frequency: LlmFrequency; country: string; enabled: boolean }>): Promise<void> {
  await req(`/api/llm-tracking/queries/${encodeURIComponent(qid)}`, { method: "PUT", body: JSON.stringify(patch) });
}
export async function deleteLlmTrackedQuery(qid: string): Promise<void> {
  await req(`/api/llm-tracking/queries/${encodeURIComponent(qid)}`, { method: "DELETE" });
}
export interface LlmRunError { platform: string; error: string; }
export async function runLlmTracking(siteKey: string): Promise<LlmTrackingData & { errors: LlmRunError[] }> {
  const r = await req<{ queries?: LlmTrackedQuery[]; snapshots?: LlmSnapshot[]; errors?: LlmRunError[] }>("/api/llm-tracking/run", { method: "POST", body: JSON.stringify({ siteKey }) });
  return { queries: r.queries ?? [], snapshots: r.snapshots ?? [], errors: r.errors ?? [] };
}
export async function runLlmQueryNow(qid: string): Promise<LlmTrackingData & { errors: LlmRunError[] }> {
  const r = await req<{ queries?: LlmTrackedQuery[]; snapshots?: LlmSnapshot[]; errors?: LlmRunError[] }>(`/api/llm-tracking/queries/${encodeURIComponent(qid)}/run`, { method: "POST", body: "{}" });
  return { queries: r.queries ?? [], snapshots: r.snapshots ?? [], errors: r.errors ?? [] };
}
// DataForSEO account balance for the low-balance warning. `configured` is false when no DFS
// credentials are saved; `ok` is false (with `error`) when the account is unreachable/blocked.
export interface DfsBalance { configured: boolean; ok?: boolean; balanceUsd?: number; error?: string; }
export async function dfsBalance(): Promise<DfsBalance> {
  return req<DfsBalance>("/api/llm-tracking/balance");
}

// ── Organic Rank Tracker ──────────────────────────────────────
export type RankFrequency = "daily" | "weekly" | "monthly";
export type RankDeviceKind = "desktop" | "mobile";
export interface RankTrackerSettings {
  id: string; siteKey: string; country: string; device: RankDeviceKind;
  depth: number; frequency: RankFrequency; enabled: boolean;
  nextRunAt: string; lastRunAt: string | null; createdAt: string;
}
export interface RankKeyword { id: string; siteKey: string; keyword: string; city: string; locationCode: number | null; createdAt: string; }
export interface RankCountry { code: number; name: string; iso: string }
export interface RankCity { code: number; name: string; type: string }
export interface RankUrlPos { url: string; position: number; }
export interface RankSnapshot {
  id: string; keywordId: string; capturedAt: string; rankings: RankUrlPos[];
  volume: number | null; cpc: number | null; serpFeatures: string[];
}
export interface RankTrackingData { settings: RankTrackerSettings | null; keywords: RankKeyword[]; snapshots: RankSnapshot[]; }

export async function getRankTracking(siteKey: string): Promise<RankTrackingData> {
  const r = await req<{ settings?: RankTrackerSettings; keywords?: RankKeyword[]; snapshots?: RankSnapshot[] }>(`/api/rank-tracking?siteKey=${encodeURIComponent(siteKey)}`);
  return { settings: r.settings ?? null, keywords: r.keywords ?? [], snapshots: r.snapshots ?? [] };
}
export async function updateRankSettings(siteKey: string, patch: Partial<{ country: string; device: RankDeviceKind; depth: number; frequency: RankFrequency; enabled: boolean }>): Promise<RankTrackerSettings> {
  const r = await req<{ settings: RankTrackerSettings }>("/api/rank-tracking/settings", { method: "PUT", body: JSON.stringify({ siteKey, ...patch }) });
  return r.settings;
}
export async function addRankKeyword(payload: { siteKey: string; keyword: string; city?: string; locationCode?: number | null }): Promise<RankKeyword> {
  const r = await req<{ keyword: RankKeyword }>("/api/rank-tracking/keywords", { method: "POST", body: JSON.stringify(payload) });
  return r.keyword;
}
// Strict location library (DataForSEO): countries + a per-country city typeahead.
export async function rankCountries(): Promise<RankCountry[]> {
  const r = await req<{ countries?: RankCountry[] }>("/api/rank-tracking/countries");
  return r.countries ?? [];
}
export async function rankCities(country: string, q: string): Promise<RankCity[]> {
  const r = await req<{ cities?: RankCity[] }>(`/api/rank-tracking/cities?country=${encodeURIComponent(country)}&q=${encodeURIComponent(q)}`);
  return r.cities ?? [];
}

// ── LLM Visibility Scanner ────────────────────────────────────
export type LlmVisKnowledgeMode = "training" | "live";
export interface LlmVisCompetitor { name: string; location: string; website: string }
export interface LlmVisConfig { id: string; siteKey: string; businessName: string; businessLocation: string; businessType: string; area: string; competitors: LlmVisCompetitor[]; knowledgeMode: LlmVisKnowledgeMode; questions: string[]; createdAt: string; updatedAt: string }
export interface LlmVisScanMeta { id: string; capturedAt: string }
export interface EntityKnowledgeCard { platform: string; hasKnowledge: boolean; confidence: string; summary: string; identity: string; knownFor: string[]; services: string[]; strengths: string[]; weaknesses: string[]; associated: string[]; reputation: string; sentiment: string; uncertainties: string[]; disambiguationRisk: string; sources: string[]; rawAnswer: string; error?: string }
export interface LlmVisMatrixRow { prompt: string; perPlatform: Record<string, { ranked: string[]; others: string[] }> }
export interface LlmVisShare { entityKey: string; appearances: number; possible: number; avgRank: number | null; score: number }
export interface LlmVisRecommendation { title: string; why: string; priority: string; actions: string[] }
export interface LlmVisEntity { key: string; name: string; location: string; website: string; isYou: boolean }
export interface LlmVisScanResult {
  generatedAt: string; knowledgeMode: LlmVisKnowledgeMode; businessType: string; area: string;
  entities: LlmVisEntity[]; knowledgeModels: string[]; matrixModels: string[];
  knowledge: Record<string, EntityKnowledgeCard[]>; matrix: LlmVisMatrixRow[]; shareOfModel: LlmVisShare[];
  recommendations: LlmVisRecommendation[]; whyCompetitorsWin: string; errors: string[];
}
export interface LlmVisibilityData { config: LlmVisConfig | null; scans: LlmVisScanMeta[]; latest: LlmVisScanResult | null }

export async function getLlmVisibility(siteKey: string): Promise<LlmVisibilityData> {
  const r = await req<{ config?: LlmVisConfig; scans?: LlmVisScanMeta[]; latest?: { result?: LlmVisScanResult } | null }>(`/api/llm-visibility?siteKey=${encodeURIComponent(siteKey)}`);
  return { config: r.config ?? null, scans: r.scans ?? [], latest: r.latest?.result ?? null };
}
export async function updateLlmVisConfig(siteKey: string, patch: Partial<{ businessName: string; businessLocation: string; businessType: string; area: string; competitors: LlmVisCompetitor[]; knowledgeMode: LlmVisKnowledgeMode; questions: string[] }>): Promise<LlmVisConfig> {
  const r = await req<{ config: LlmVisConfig }>("/api/llm-visibility/config", { method: "PUT", body: JSON.stringify({ siteKey, ...patch }) });
  return r.config;
}
export async function runLlmVisibilityScan(siteKey: string): Promise<{ result: LlmVisScanResult; scanId: string; capturedAt: string; scans: LlmVisScanMeta[] }> {
  const r = await req<{ result: LlmVisScanResult; scanId: string; capturedAt: string; scans?: LlmVisScanMeta[] }>("/api/llm-visibility/scan", { method: "POST", body: JSON.stringify({ siteKey }) });
  return { result: r.result, scanId: r.scanId, capturedAt: r.capturedAt, scans: r.scans ?? [] };
}
export async function getLlmVisScan(id: string): Promise<LlmVisScanResult | null> {
  const r = await req<{ result?: LlmVisScanResult }>(`/api/llm-visibility/scan/${encodeURIComponent(id)}`);
  return r.result ?? null;
}

// ── AI-Crawlability & Technical Check ────────────────────────────
export type CrawlSeverity = "critical" | "warning" | "ok" | "info";
export type CrawlRenderMode = "ssr" | "hybrid" | "js-only" | "unknown";
export interface CrawlabilityFinding { id: string; area: string; severity: CrawlSeverity; title: string; detail: string; evidence: string; fix: string }
export interface CrawlRobotStatus { token: string; vendor: string; purpose: string; major: boolean; status: "allowed" | "disallowed" | "no-rule"; directive: string | null; line: number | null; source: string }
export interface CrawlBotProbe { token: string; vendor: string; normalStatus: number; botStatus: number; blocked: boolean; note: string }
export interface CrawlabilityResult {
  capturedAt: string; siteUrl: string; base: string;
  summary: { critical: number; warning: number; ok: number; info: number };
  findings: CrawlabilityFinding[];
  robots: { present: boolean; url: string; fetchStatus: number; bots: CrawlRobotStatus[]; sitemaps: string[] };
  botAccess: { normalStatus: number; normalOk: boolean; vendorHint: string | null; probes: CrawlBotProbe[] };
  rendering: { renderMode: CrawlRenderMode; renderedWords: number; appRootMarker: string | null; homepageStatus: number };
  schema: { types: string[]; hasBusinessEntity: boolean; error?: string };
  sitemap: { found: boolean; url: string | null; source: "robots" | "probe" | null };
  llmsTxt: { found: boolean; url: string };
  aiExplanation: string | null;
  errors: string[];
}
export interface CrawlabilityCheckMeta { id: string; capturedAt: string }
export interface CrawlabilityData { checks: CrawlabilityCheckMeta[]; latest: CrawlabilityResult | null; latestId: string | null }

export async function getCrawlability(siteKey: string): Promise<CrawlabilityData> {
  const r = await req<{ checks?: CrawlabilityCheckMeta[]; latest?: { id?: string; result?: CrawlabilityResult } | null }>(`/api/crawlability?siteKey=${encodeURIComponent(siteKey)}`);
  return { checks: r.checks ?? [], latest: r.latest?.result ?? null, latestId: r.latest?.id ?? null };
}
export async function runCrawlabilityCheck(siteKey: string, siteUrl: string): Promise<{ result: CrawlabilityResult; checkId: string; capturedAt: string; checks: CrawlabilityCheckMeta[] }> {
  const r = await req<{ result: CrawlabilityResult; checkId: string; capturedAt: string; checks?: CrawlabilityCheckMeta[] }>("/api/crawlability/check", { method: "POST", body: JSON.stringify({ siteKey, siteUrl }) });
  return { result: r.result, checkId: r.checkId, capturedAt: r.capturedAt, checks: r.checks ?? [] };
}
export async function explainCrawlability(checkId: string): Promise<string> {
  const r = await req<{ text: string }>("/api/crawlability/explain", { method: "POST", body: JSON.stringify({ checkId }) });
  return r.text;
}
export async function getCrawlabilityCheck(id: string): Promise<CrawlabilityResult | null> {
  const r = await req<{ result?: CrawlabilityResult }>(`/api/crawlability/check/${encodeURIComponent(id)}`);
  return r.result ?? null;
}

// ── History / Activity log ────────────────────────────────────
export type HistoryAction = "new_page" | "new_service_area_page" | "schema_added" | "internal_links_added" | "meta_updated" | "redirect_added" | "lhk_added";
export interface HistoryEntry { id: string; action: HistoryAction; url: string; actorEmail: string; pageId: number | null; siteKey: string | null; createdAt: string }

// Best-effort: the WordPress write is the source of truth, so a failed history log must never break or
// revert an apply. Swallows all errors.
export async function recordHistory(input: { action: HistoryAction; url: string; siteKey?: string; pageId?: number | null }): Promise<void> {
  try { await req("/api/history", { method: "POST", body: JSON.stringify(input) }); }
  catch { /* logging is non-critical */ }
}
export async function listHistory(siteKey?: string): Promise<HistoryEntry[]> {
  const r = await req<{ entries?: HistoryEntry[] }>(`/api/history${siteKey ? `?siteKey=${encodeURIComponent(siteKey)}` : ""}`);
  return r.entries ?? [];
}

// ── Niche Ads Scraper ────────────────────────────────────────
export interface NicheAdsCity { label: string; locationCode?: number; lat?: number; lng?: number }
export interface NicheAdsConfig {
  id: string; siteKey: string; keywords: string[]; cities: NicheAdsCity[]; country: string; devices: string[]; surfaces: string[]; depth: number; pages: number;
  scheduleEnabled: boolean; scheduleDays: number[]; scheduleTimes: string[]; scheduleTz: string; nextRunAt: string | null; lastRunAt: string | null; createdAt: string; updatedAt: string;
}
export type NicheAdsRunStatus = "queued" | "running" | "done" | "error" | "interrupted";
export interface NicheAdsRunMeta { id: string; status: NicheAdsRunStatus; trigger: string; total: number; done: number; createdAt: string; finishedAt: string | null }
export interface NicheAdsDomain { domain: string; adTypes: string[]; keywords: string[]; cities: string[]; devices: string[]; appearances: number; position?: string; gbpUrl?: string; firmName?: string; rating?: number | null; reviews?: number | null; phone?: string; sampleTitle?: string; sampleUrl?: string }
export interface NicheAdsKeywordStat { keyword: string; volume: number | null; cpc: number | null }
export interface NicheAdsDebug { tasksOk: number; tasksFailed: number; statusCodes: Record<string, number>; itemTypes: Record<string, number>; sampleError?: string }
export interface NicheAdsResult { stats: { cities: number; keywords: number; devices: number; searches: number; adsFound: number }; keywords: NicheAdsKeywordStat[]; domains: NicheAdsDomain[]; debug?: NicheAdsDebug }
export interface NicheAdsRun extends NicheAdsRunMeta { config: unknown; result: NicheAdsResult | null; error: string | null; updatedAt: string }
export interface NicheAdsData { config: NicheAdsConfig | null; runs: NicheAdsRunMeta[]; latest: NicheAdsRun | null }
export interface AdvertiserCreative { advertiserId: string; creativeId: string; title: string; url: string; format: string; firstShown: string | null; lastShown: string | null; previewImage: string | null }

export async function getNicheAds(siteKey: string): Promise<NicheAdsData> {
  const r = await req<{ config?: NicheAdsConfig; runs?: NicheAdsRunMeta[]; latest?: NicheAdsRun | null }>(`/api/niche-ads?siteKey=${encodeURIComponent(siteKey)}`);
  return { config: r.config ?? null, runs: r.runs ?? [], latest: r.latest ?? null };
}
export async function updateNicheAdsConfig(siteKey: string, patch: Partial<{ keywords: string[]; cities: NicheAdsCity[]; country: string; devices: string[]; surfaces: string[]; depth: number; pages: number; scheduleEnabled: boolean; scheduleDays: number[]; scheduleTimes: string[]; scheduleTz: string }>): Promise<NicheAdsConfig> {
  const r = await req<{ config: NicheAdsConfig }>("/api/niche-ads/config", { method: "PUT", body: JSON.stringify({ siteKey, ...patch }) });
  return r.config;
}
export async function runNicheAds(siteKey: string): Promise<{ runId: string; total: number }> {
  return req<{ runId: string; total: number }>("/api/niche-ads/run", { method: "POST", body: JSON.stringify({ siteKey }) });
}
export async function getNicheAdsRun(id: string): Promise<NicheAdsRun | null> {
  const r = await req<{ run?: NicheAdsRun }>(`/api/niche-ads/run/${encodeURIComponent(id)}`);
  return r.run ?? null;
}
export async function verifyAdvertiser(domain: string, country: string): Promise<AdvertiserCreative[]> {
  const r = await req<{ creatives?: AdvertiserCreative[] }>("/api/niche-ads/verify", { method: "POST", body: JSON.stringify({ domain, country }) });
  return r.creatives ?? [];
}
export interface RadiusCity { city: string; state: string; lat: number; lng: number; distance: number }
export async function geocodeAddress(address: string): Promise<{ lat: number; lng: number; cityName: string } | null> {
  const r = await req<{ ok?: boolean; lat?: number; lng?: number; cityName?: string }>("/api/geocode", { method: "POST", body: JSON.stringify({ address }) });
  return r.ok && typeof r.lat === "number" && typeof r.lng === "number" ? { lat: r.lat, lng: r.lng, cityName: r.cityName ?? "" } : null;
}
export async function citiesInRadius(lat: number, lng: number, miles: number): Promise<RadiusCity[]> {
  const r = await req<{ cities?: RadiusCity[] }>("/api/cities-in-radius", { method: "POST", body: JSON.stringify({ lat, lng, miles }) });
  return r.cities ?? [];
}
export async function deleteRankKeyword(kid: string): Promise<void> {
  await req(`/api/rank-tracking/keywords/${encodeURIComponent(kid)}`, { method: "DELETE" });
}
export async function runRankKeyword(kid: string): Promise<RankTrackingData> {
  const r = await req<{ settings?: RankTrackerSettings; keywords?: RankKeyword[]; snapshots?: RankSnapshot[] }>(`/api/rank-tracking/keywords/${encodeURIComponent(kid)}/run`, { method: "POST", body: "{}" });
  return { settings: r.settings ?? null, keywords: r.keywords ?? [], snapshots: r.snapshots ?? [] };
}
export interface SerpLocalBusiness { title: string; position: number; rating: number | null }
export interface SerpOrganicResult { position: number; title: string; url: string; domain: string }
export interface SerpBreakdown { ok: boolean; aiOverview: boolean; featured: string[]; localPack: SerpLocalBusiness[]; paa: string[]; organic: SerpOrganicResult[] }
export async function rankKeywordSerp(kid: string): Promise<SerpBreakdown> {
  const r = await req<{ serp: SerpBreakdown }>(`/api/rank-tracking/keywords/${encodeURIComponent(kid)}/serp`, { method: "POST", body: "{}" });
  return r.serp;
}
export async function runRankTracking(siteKey: string): Promise<RankTrackingData & { errors: string[] }> {
  const r = await req<{ settings?: RankTrackerSettings; keywords?: RankKeyword[]; snapshots?: RankSnapshot[]; errors?: string[] }>("/api/rank-tracking/run", { method: "POST", body: JSON.stringify({ siteKey }) });
  return { settings: r.settings ?? null, keywords: r.keywords ?? [], snapshots: r.snapshots ?? [], errors: r.errors ?? [] };
}
export async function rankBalance(): Promise<DfsBalance> {
  return req<DfsBalance>("/api/rank-tracking/balance");
}

// Live JSON-LD rendered on a public page (RankMath/Yoast/theme schema included) — the Schema tab
// reconciles an Approve against this so it never adds a block that already exists or a business
// entity that competes with one already on the page. Best-effort: returns empty on any failure.
export interface LivePageSchemaResult { types: string[]; blocks: Record<string, unknown>[] }
export async function fetchLivePageSchema(url: string): Promise<LivePageSchemaResult> {
  try {
    const r = await req<{ ok: boolean; types?: string[]; blocks?: Record<string, unknown>[] }>(
      "/api/page-schema", { method: "POST", body: JSON.stringify({ url }) },
    );
    return { types: r.types ?? [], blocks: r.blocks ?? [] };
  } catch { return { types: [], blocks: [] }; }
}

// Pages already inserted into via the Low-Hanging KW tool (per user + site).
export async function listLhkInserted(siteKey: string): Promise<string[]> {
  const { pages } = await req<{ pages: string[] }>(`/api/lhk-inserted?siteKey=${encodeURIComponent(siteKey)}`);
  return pages ?? [];
}
export interface LhkInsertedPage { url: string; title: string; type: string; editedAt: string; keywords: string[]; pageId?: number; }
// Full history records for the "History — Optimized Pages" list.
export async function listLhkInsertedHistory(siteKey: string): Promise<LhkInsertedPage[]> {
  const { pages } = await req<{ pages: LhkInsertedPage[] }>(`/api/lhk-inserted/history?siteKey=${encodeURIComponent(siteKey)}`);
  return pages ?? [];
}
export async function markLhkInserted(siteKey: string, page: string, title = "", pageType = "", keywords: string[] = [], pageId?: number): Promise<void> {
  await req("/api/lhk-inserted", { method: "POST", body: JSON.stringify({ siteKey, page, title, pageType, keywords, pageId }) });
}
// Fill in title/type (and WP page id, when known) on an existing history record without changing its edited date.
export async function backfillLhkInserted(siteKey: string, page: string, title: string, pageType: string, pageId?: number): Promise<void> {
  await req("/api/lhk-inserted/backfill", { method: "POST", body: JSON.stringify({ siteKey, page, title, pageType, pageId }) });
}

// ── GBP Review Manager ────────────────────────────────────────
export type GbpFullFreq = "weekly" | "biweekly" | "monthly";
export type GbpNewFreq = "daily" | "every_3_days" | "weekly" | "biweekly";
export interface GbpCandidate { cid: string; name: string; address: string; rating: number | null; reviewCount: number | null; profileUrl: string; }
export interface GbpHoursDay { day: string; text: string }
export interface GbpProfile {
  siteId: string; cid: string; businessName: string; address: string; profileUrl: string;
  rating: number | null; reviewCount: number | null;
  fullFrequency: GbpFullFreq; newFrequency: GbpNewFreq;
  fullNextRunAt: string; newNextRunAt: string; fullLastRunAt: string; newLastRunAt: string; enabled: boolean;
  category: string; additionalCategories: string[]; phone: string; hours: GbpHoursDay[];
}
export interface GbpReview {
  reviewId: string; reviewUrl: string; rating: number; reviewDate: string; text: string; author: string; authorImage: string;
  ownerReply: string; ownerReplyDate: string; replyDraft: string; firstSeen: string; lastSeen: string;
  removed: boolean; removedAt: string;
}
export interface GbpData { profile: GbpProfile | null; reviews: GbpReview[]; removed: GbpReview[] }

export async function searchGbpBusiness(query: string, location?: string): Promise<GbpCandidate[]> {
  const r = await req<{ candidates?: GbpCandidate[] }>("/api/gbp/search", { method: "POST", body: JSON.stringify({ query, location }) });
  return r.candidates ?? [];
}
export async function saveSiteGbp(id: string, sel: GbpCandidate & { fullFrequency?: GbpFullFreq; newFrequency?: GbpNewFreq }): Promise<void> {
  await req(`/api/sites/${encodeURIComponent(id)}/gbp`, {
    method: "PUT",
    body: JSON.stringify({ cid: sel.cid, name: sel.name, address: sel.address, profileUrl: sel.profileUrl, rating: sel.rating, reviewCount: sel.reviewCount, fullFrequency: sel.fullFrequency, newFrequency: sel.newFrequency }),
  });
}
export async function disconnectSiteGbp(id: string): Promise<void> {
  await req(`/api/sites/${encodeURIComponent(id)}/gbp`, { method: "DELETE" });
}
export async function getGbp(siteKey: string): Promise<GbpData> {
  const r = await req<{ profile?: GbpProfile | null; reviews?: GbpReview[]; removed?: GbpReview[] }>(`/api/gbp?siteKey=${encodeURIComponent(siteKey)}`);
  return { profile: r.profile ?? null, reviews: r.reviews ?? [], removed: r.removed ?? [] };
}
export async function updateGbpFrequency(siteKey: string, patch: { fullFrequency?: GbpFullFreq; newFrequency?: GbpNewFreq; enabled?: boolean }): Promise<void> {
  await req("/api/gbp/frequency", { method: "PUT", body: JSON.stringify({ siteKey, ...patch }) });
}
// The crawl now runs in the BACKGROUND (large profiles can take minutes) — this returns as soon as
// it's started, with the site's CURRENT data; callers poll getGbp() for the results as they land.
export async function runGbpCrawl(siteKey: string, mode: "full" | "new"): Promise<GbpData & { started: boolean; alreadyRunning: boolean }> {
  const r = await req<{ profile?: GbpProfile | null; reviews?: GbpReview[]; removed?: GbpReview[]; started?: boolean; alreadyRunning?: boolean }>("/api/gbp/run", { method: "POST", body: JSON.stringify({ siteKey, mode }) });
  return { profile: r.profile ?? null, reviews: r.reviews ?? [], removed: r.removed ?? [], started: !!r.started, alreadyRunning: !!r.alreadyRunning };
}
export async function saveGbpReply(siteKey: string, reviewId: string, draft: string): Promise<void> {
  await req(`/api/gbp/reviews/${encodeURIComponent(reviewId)}/reply`, { method: "PUT", body: JSON.stringify({ siteKey, draft }) });
}

// ── API keys ──────────────────────────────────────────────────
export async function fetchApiKeys(): Promise<ApiKeys> {
  const { keys } = await req<{ keys: ApiKeys }>("/api/keys");
  return keys;
}

export async function putApiKeys(patch: Partial<ApiKeys>): Promise<void> {
  await req("/api/keys", { method: "PUT", body: JSON.stringify(patch) });
}
